import React, { useState, useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import {
  Grid,
  Radio,
  RadioGroup,
  Button,
  TextField,
  FormControl,
  FormLabel,
  InputAdornment,
  DialogContentText,
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardMedia,
  Typography,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BusinessIcon from '@mui/icons-material/Business';
import DraftsIcon from '@mui/icons-material/Drafts';
import HomeIcon from '@mui/icons-material/Home';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import AddIcCallIcon from '@mui/icons-material/AddIcCall';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import FaxIcon from '@mui/icons-material/Fax';
import LinkIcon from '@mui/icons-material/Link';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { merchantSchema } from '../../../schemas/merchantSchema';
import { swalErrorBtn } from '../../../helpers/swalHelper';
import debouncedHelper from '../../../helpers/debouncedHelper';
import CompanyListApi from '../../../service/company.api';
import CropModal from '../../CropModal';
import { omit } from 'lodash';

export default function MerchantDialog({ open, onClose, mode, defaultValues, onSubmit }) {

    const [urlPath, setURLPath] = useState('');
    const [taxId, setTaxId] = useState('');
    const [errorPath, setErrorPath] = useState(false);
    const [taxIdError, setTaxIdError] = useState(false);
    
    const [templateList ,setTemplateList] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('Default');
    const [selectedReceiptTemplate, setSelectedReceiptTemplate] = useState('Default');

    const [imageLogo, setImageLogo] = useState('');
    const [logoBlob, setLogoBlob] = useState(null);
    const [cropSrc, setCropSrc] = useState(null);
    const [showCropper, setShowCropper] = useState(false);

    const { control, formState: { errors } ,handleSubmit, reset, register, setValue, getValues, watch } = useForm({
        defaultValues: {
            ...defaultValues,
            isJV: !!defaultValues?.company_code,
            tax_id: defaultValues?.tax_id || '',
            allow_edit: typeof defaultValues?.allow_edit === 'boolean' ? defaultValues.allow_edit : (mode !== 'edit'),
            haveBizId: typeof defaultValues?.haveBizId === 'boolean' ? defaultValues.haveBizId : (mode !== 'edit'),
            pdf_template_name: defaultValues?.pdf_template_name || { invoice: '', receipt: '' },
        },
        resolver: yupResolver(merchantSchema())
    });

    // console.log("defaultValues", defaultValues);
    // const urlPath = watch("url_path");
    const isJV = watch("isJV");
    const status_etax = watch("status_etax");
    const have_pdf = watch("have_pdf");
    const have_receipt = watch("have_receipt");

    const receiptTemplateList = useMemo(() => {
    return templateList.filter((templateName) => {
        return (templateName === 'Default') || (templateName === selectedTemplate);
    })
    }, [selectedTemplate, templateList]);
    
    const getTemplateList = async () => {
        try {
            const resp = await CompanyListApi.getTemplateApi();

            setTemplateList(resp.data);
        } catch (error) {
            console.log("error", error);
        }
    };

    const handleTaxId = (e) => {
        const input = e.target.value
        const numericValue = input.replace(/\D/g, '');
        const trimmedValue = numericValue.slice(0, 13);
        setTaxId(trimmedValue);
    }

    const checkErrorTaxId = async (e) => {
        const inputTaxId = e.target.value;
        // console.log("getTaxid",getValues('tax_id'))
        if ( inputTaxId.length !== 13 ) {
            setTaxIdError(true);
        }

        if ( inputTaxId === getValues('tax_id')) {
            setTaxIdError(false);
            return;
        }
        try {
            const company = await CompanyListApi.getTaxIdCompanyListApi();
            const isExists = company.some((c) => c.tax_id === inputTaxId)
            // console.log("isexists", isExists);
            setTaxIdError(isExists === true);
            return !isExists
        } catch (error) {
          console.log("Error : ", error);
        }
    }

    const debouncedInputTaxId = useMemo(() => debouncedHelper(checkErrorTaxId, 750), []);

    const handleURLPath = (e) => {
        const inputPath = e.target.value;
        const thaiPattern = /[\u0E00-\u0E7F]/;
        const pathReplace = inputPath.toLowerCase().replace(/\s+/g, "-");
        setURLPath(pathReplace.replace(thaiPattern, ""));
    }
    
    const checkErrorURL = async (e) => {
        const inputPath = e.target.value;
        if ( inputPath === getValues('url_path') ) {
            setErrorPath(false);
            return;
        }
        try {
          const resp = await CompanyListApi.getCheckPathMerchant(inputPath);
          setErrorPath(resp.data.isExists === true);
        } catch (error) {
          console.log("Error : ", error);
        }
    }
    
    const debouncedInputPath = useMemo(() => debouncedHelper(checkErrorURL, 750), [])

    const buildBlobFromLogo = (logo) => {
        if (!logo) return null;
        // console.log("logo", logo)
        const data = Array.isArray(logo.data) ? logo.data : (Array.isArray(logo) ? logo : null);
        // console.log("data", data)
        if (!data) return null;
        try {
            const uint8 = new Uint8Array(data);
            return new Blob([uint8], { type: 'image/png' });
        } catch (err) {
            console.warn('buildBlobFromLogo error', err);
            return null;
        }
    }

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        // console.log("file", file);
        if (!file) return;

        //base64
        const reader = new FileReader();
        reader.onload = () => {
            setCropSrc(reader.result);
            setShowCropper(true);
            e.target.value = ''
        };
        reader.readAsDataURL(file);
    };
    
    const onCropComplete = (blob) => {
        setLogoBlob(blob);
        setShowCropper(false);
    };

    useEffect(() => {
        if (open) {
            reset({
                ...defaultValues,
                isJV: !!defaultValues?.company_code,
                tax_id: defaultValues?.tax_id || '',
                allow_edit: typeof defaultValues?.allow_edit === 'boolean' ? defaultValues.allow_edit : (mode !== 'edit'),
                haveBizId: typeof defaultValues?.haveBizId === 'boolean' ? defaultValues.haveBizId : (mode !== 'edit'),
                pdf_template_name: defaultValues?.pdf_template_name || { invoice: '', receipt: '' },
            });
            setSelectedTemplate(getValues('pdf_template_name.invoice') || 'Default');
            setSelectedReceiptTemplate(getValues('pdf_template_name.receipt') || 'Default');
            setTaxId(getValues('tax_id'));
            setURLPath(getValues('url_path'));
            const blobFromDefault = buildBlobFromLogo(defaultValues?.logo);
            setLogoBlob(blobFromDefault);
            getTemplateList();
        }
    }, [open, defaultValues, reset]);

    useEffect(() => {
        if (!logoBlob) return
        const url = URL.createObjectURL(logoBlob)
        setImageLogo(url)

        return () => URL.revokeObjectURL(url)
    }, [logoBlob])

    const handleSave = async (data) => {
        // console.log("data handleSave", data);
        if (taxIdError || errorPath || taxId.length !== 13) {
            return;
        }

        if (taxId) {
            data.tax_id = taxId;
        };

        if (data.ops_type === 'NEWOPS' && !data.new_ops ) {
            return;
        };

        if (!data.pdf_template_name || typeof data.pdf_template_name !== 'object') {
            data.pdf_template_name = { invoice: '', receipt: '' };
        }

        if (!data.isJV) {
            data.company_code = '';
            data.account_contact = '';
        };

        if (urlPath) {
            data.url_path = urlPath;
        };

        if (have_pdf) {
            data.pdf_template_name.invoice = selectedTemplate || null;
        } else {
            data.pdf_template_name.invoice = null;
        }

        if (have_receipt) {
            data.pdf_template_name.receipt = selectedReceiptTemplate || null;
        } else {
            data.pdf_template_name.receipt = null;
        }

        if (data.logo) {
            data = omit(data, ['logo']);
        }
        onSubmit(data, logoBlob);
    };

    const handleClose = () => {
        onClose()
        handleClear()
    }

    const handleClear = () => {
        reset({
            name: '',
            name_en: '',
            tax_id: '',
            company_Email: '',
            key_ops: '',
            address_th: '',
            address_en: '',
            tel: '',
            fax: '',
            url_path: '',
            ops_type: 'NO',
            new_ops: '',
            allow_edit: (mode !== 'edit'),
            have_pdf: false,
            have_receipt: false,
            allow_manual_payment: false,
            haveBizId: (mode !== 'edit'),
            isJV: false,
            company_code: '',
            account_contact: '',
            status_etax: false,
            branch_id: '',
            key_api: '',
            pdf_template_name: { invoice: '', receipt: '' },
        });

        setLogoBlob(null);
        setImageLogo('');
        setCropSrc(null);
        setShowCropper(false);
        setSelectedTemplate('Default');
        setSelectedReceiptTemplate('Default');
        setURLPath('');
        setTaxId('');
        setErrorPath(false);
    };

  return (
    <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ style: { minWidth: 'auto', maxWidth: 'auto' } }}
    >
      <DialogTitle> {mode === 'edit' ? 'แก้ไขร้านค้า' : 'สร้างรายการร้านค้า'} </DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
            <Grid container spacing={2} sx={{ textAlign: 'center' }}>
                {/* Text Field */}
                <Grid item xs={12} md={6} sx={{ marginTop: '10px' }}>
                    <TextField 
                        {...register("name")}
                        fullWidth 
                        label="ชื่อบริษัท (ภาษาไทย)"
                        placeholder="กรอกชื่อบริษัท (ภาษาไทย)"
                        variant="outlined"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <BusinessIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ marginTop: '10px' }}>
                    <TextField 
                        {...register("name_en")}
                        fullWidth 
                        label="ชื่อบริษัท (ภาษาอังกฤษ)"
                        placeholder="กรอกชื่อบริษัท (ภาษาอังกฤษ)"
                        variant="outlined"
                        error={!!errors.name_en}
                        helperText={errors.name_en?.message}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <BusinessIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ marginTop: '5px' }}>
                    <TextField 
                        // {...register("tax_id")}
                        fullWidth 
                        label="เลขประจำตัวผู้เสียภาษี"
                        placeholder="กรอกตัวเลข 13 หลัก"
                        variant="outlined"
                        value={taxId}
                        onChange={handleTaxId}
                        onInput={debouncedInputTaxId}
                        // error={!!errors.tax_id}
                        // helperText={errors.tax_id?.message}
                        error={taxId.length != 13 || taxIdError }
                        helperText={taxId.length != 13 ? 'กรุณากรอกเลขประจำตัวผู้เสียภาษี 13 หลัก' : taxIdError ? 'มีผู้ใช้นี้แล้วในระบบ' : ''}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DescriptionIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ marginTop: '5px' }}>
                    <TextField 
                        {...register("company_Email")}
                        fullWidth 
                        label="อีเมลล์"
                        placeholder="กรอกอีเมลล์"
                        variant="outlined"
                        error={!!errors.company_Email}
                        helperText={errors.company_Email?.message}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DraftsIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={12} sx={{ marginTop: '5px' }}>
                    <TextField 
                        {...register("key_ops")}
                        fullWidth 
                        label="Key Ops"
                        placeholder="กรอก Key Ops"
                        variant="outlined"
                        error={!!errors.key_ops}
                        helperText={errors.key_ops?.message}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <VpnKeyIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={12} sx={{ marginTop: '5px' }}>
                    <TextField 
                        {...register("address_th")}
                        fullWidth 
                        label="ที่อยู่ (ภาษาไทย)"
                        placeholder="กรอกที่อยู่ (ภาษาไทย)"
                        variant="outlined"
                        error={!!errors.address_th}
                        helperText={errors.address_th?.message}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <HomeIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={12} sx={{ marginTop: '5px' }}>
                    <TextField 
                        {...register("address_en")}
                        fullWidth 
                        label="ที่อยู่ (ภาษาอังกฤษ)"
                        placeholder="กรอกที่อยู่ (ภาษาอังกฤษ)"
                        variant="outlined"
                        error={!!errors.address_en}
                        helperText={errors.address_en?.message}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <HomeIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ marginTop: '5px' }}>
                    <TextField 
                        {...register("tel")}
                        fullWidth 
                        label="เบอร์โทรศัพท์"
                        placeholder="กรอกเบอร์โทรศัพท์"
                        variant="outlined"
                        error={!!errors.tel}
                        helperText={errors.tel?.message}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AddIcCallIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ marginTop: '5px' }}>
                    <TextField 
                        {...register("fax")}
                        fullWidth 
                        label="Fax"
                        placeholder="กรอกเบอร์ Fax"
                        variant="outlined"
                        error={!!errors.fax}
                        helperText={errors.fax?.message}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FaxIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={12} sx={{ marginTop: '5px' }}>
                    <TextField 
                        value={urlPath}
                        onChange={handleURLPath}
                        onInput={debouncedInputPath}
                        fullWidth
                        label="Path URL Website"
                        placeholder="ตัวอย่าง : example-name"
                        variant="outlined"
                        error={errorPath}
                        helperText={errorPath ? 'Path นี้ถูกใช้งานไปแล้ว' : ''}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LinkIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <span className='flex justify-start text-sm mt-2 text-[#4FA893]'>
                        Example : https://paymentlite.inet.co.th/
                        <p className='text-red-500'>{urlPath}</p>
                    </span>
                </Grid>
                {/* Radio */}
                <Grid item xs={12} md={12} lg={12} sx={{ marginTop: '5px', display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                <FormControl sx={{ width: "100%" }}>
                    <FormLabel sx={{ textAlign: "left" }}> Key ในการสร้าง QR Code </FormLabel>
                    <Controller
                        name="ops_type"
                        control={control}
                        render={({ field }) => (
                        <RadioGroup
                            {...field}
                            sx={{ width: "100%" }}
                            >
                            <FormControlLabel value="NO" control={<Radio />} label="ไม่มี QR Code" />
                            <FormControlLabel value="OPS" control={<Radio />} label="Ops เก่า" />
                            <Box display="flex" gap={2} alignItems="center">
                                <FormControlLabel value="NEWOPS" control={<Radio />} label="Ops ใหม่" />
                                {field.value === "NEWOPS" && (
                                    <Controller name="new_ops" control={control}
                                        render={({ field }) => (
                                            <TextField
                                            {...field}
                                            label="Key Ops ใหม่"
                                            variant="standard"
                                            placeholder="กรอก Key Ops ใหม่"
                                            InputProps={{
                                                startAdornment: (
                                                <InputAdornment position="start">
                                                    <VpnKeyIcon />
                                                </InputAdornment>
                                                ),
                                            }}
                                            error={!field.value}
                                            helperText={!field.value ? "กรุณากรอก Key Ops ใหม่" : ""}
                                            />
                                        )}
                                    />
                                )}
                            </Box>
                        </RadioGroup>
                        )}
                    />
                    {errors.ops_type && <p className='text-red-500 flex justify-start text-xs p-1'>{errors.ops_type.message}</p>}
                </FormControl>
                </Grid>
                {/* CheckBox */}
                <Grid item xs={12} md={12} lg={12} sx={{ marginTop: '5px', display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                <FormControl sx={{ width: '100%' }}>
                    <FormGroup>
                        <Controller name="allow_edit" control={control}
                            render={({ field }) => (
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={!!field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    /> }
                                label="อนุญาตการแก้ไขข้อมูล (หลังอัปโหลด Excel)"
                                />
                            )}
                        />
                    </FormGroup>
                </FormControl>
                </Grid>
                <Grid item xs={12} md={6} lg={6} sx={{ marginTop: '5px', display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                <FormControl sx={{ width: '100%' }}>
                    <FormGroup>
                    <Controller name="have_pdf" control={control}
                        render={({ field }) => (
                            <FormControlLabel control={
                                <Checkbox
                                    checked={!!field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                /> }
                            label="มีใบแจ้งหนี้ PDF"
                            />
                        )}
                    />
                    <Controller name="have_receipt" control={control}
                        render={({ field }) => (
                            <FormControlLabel control={
                                <Checkbox
                                    checked={!!field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                /> }
                            label="มีใบเสร็จชำระเงิน"
                            />
                        )}
                    />
                    </FormGroup>
                </FormControl>
                </Grid>
                <Grid item xs={12} md={6} lg={6} sx={{ marginTop: '5px', display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                <FormControl sx={{ width: '100%' }}>
                    <FormGroup>
                        <Controller name="allow_manual_payment" control={control}
                            render={({ field }) => (
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={!!field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    /> }
                                label="อนุญาตการจ่ายเงินแบบ Manual"
                                />
                            )}
                        />
                        <Controller name="haveBizId" control={control}
                            render={({ field }) => (
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={!!field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    /> }
                                label="Business id (RA)"
                                />
                            )}
                        />
                    </FormGroup>
                </FormControl>
                </Grid>

                {have_pdf && (
                <Grid item xs={12} md={6} lg={6} sx={{ marginTop: '5px', display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                    <FormControl fullWidth>
                    <InputLabel>เลือก Template PDF</InputLabel>
                    <Select
                        value={selectedTemplate}
                        label="เลือก Template PDF"
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                    >
                        {templateList.map((name, index) => (
                        <MenuItem key={index} value={name}>
                            {name}
                        </MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                )}

                {have_receipt && (
                <Grid item xs={12} md={6} lg={6} sx={{ marginTop: '5px', display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                    <FormControl fullWidth>
                        <InputLabel>เลือก Template ใบเสร็จ</InputLabel>
                        <Select
                        value={selectedReceiptTemplate}
                        label="เลือก Template ใบเสร็จ"
                        onChange={(e) => setSelectedReceiptTemplate(e.target.value)}
                        >
                        {receiptTemplateList.map((name, index) => (
                            <MenuItem key={index} value={name}>
                            {name}
                            </MenuItem>
                        ))}
                        </Select>
                    </FormControl>
                </Grid>
                )}

                {(have_pdf || have_receipt) && (
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" alignItems="start">
                    <input
                        accept="image/*"
                        id="upload-image"
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleImageChange}
                    />
                    <label htmlFor="upload-image">
                        <Card
                        sx={{
                            width: 280,
                            height: 180,
                            border: "2px dashed #B0B0B0",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#f5f5f5" },
                        }}
                        >
                        {imageLogo ? (
                            <CardMedia
                            component="img"
                            sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                            }}
                            image={imageLogo}
                            alt="Uploaded preview"
                            />
                        ) : (
                            <Box textAlign="center">
                                <CloudUploadIcon color="primary" sx={{ fontSize: 30 }} />
                                <Typography variant="body2" color="textSecondary">
                                    Upload Logo
                                </Typography>
                            </Box>
                        )}
                        </Card>
                    </label>
                    </Box>
                </Grid>
                )}

                {showCropper && (
                    <CropModal
                        open={showCropper}
                        imageSrc={cropSrc}
                        onCancel={() => {
                            setCropSrc(null)
                            setShowCropper(false)
                        }}
                        onComplete={onCropComplete}
                    />
                )}
                
                {/* CheckBox IsJV */}
                <Grid item xs={12} md={12} lg={12} sx={{ marginTop: '5px', display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                <FormControl sx={{ width: '100%' }}>
                    <FormGroup>
                        <Controller name="isJV" control={control}
                            render={({ field }) => (
                                <FormControlLabel 
                                control={ <Checkbox checked={!!field.value} {...field}/> }
                                label="JV"
                                />
                            )}
                        />
                    </FormGroup>
                </FormControl>
                </Grid>
                {isJV && (
                    <>
                    <Grid item xs={12} md={6} sx={{ marginTop: '5px' }}>
                        <TextField
                            fullWidth
                            {...register("company_code")}
                            label="Company code"
                            variant="outlined"
                            placeholder="กรอก Company code"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <DraftsIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ marginTop: '5px' }}>
                        <TextField
                            fullWidth
                            {...register("account_contact")}
                            label="Account Contact"
                            variant='outlined'
                            placeholder='กรอก Account Contact'
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <ContactPageIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    </>
                )}

                <Grid item xs={12} md={12} lg={12} sx={{ marginTop: '5px', display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                <FormControl sx={{ width: '100%' }}>
                    <FormGroup>
                        <Controller name="status_etax" control={control}
                            render={({ field }) => (
                                <FormControlLabel 
                                control={ <Checkbox checked={!!field.value} {...field} /> }
                                label="ใบกำกับภาษีอิเล็กทรอนิกส์"
                                />
                            )}
                        />
                    </FormGroup>
                </FormControl>
                </Grid>
                {status_etax && (
                <>
                    <Grid item xs={12} md={12} sx={{ marginTop: '5px' }}>
                        <TextField
                            {...register('branch_id')}
                            fullWidth
                            label="เลขที่สาขา"
                            variant="outlined"
                            placeholder="กรอก Branch ID"
                            error={!!errors.branch_id}
                            helperText={errors.branch_id?.message}
                            InputProps={{
                                startAdornment: (
                                <InputAdornment position="start">
                                    <DraftsIcon />
                                </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={12} sx={{ marginTop: '5px' }}>
                        <TextField
                            fullWidth
                            {...register('key_api')}
                            label="API Key"
                            variant="outlined"
                            placeholder="API Key"
                            error={!!errors.key_api}
                            helperText={errors.key_api?.message}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <DraftsIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </>
                )}
            </Grid>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
            onClick={handleSubmit(handleSave)}
            color="primary"
            // disabled={!urlPath}
        >
          {mode === 'edit' ? 'บันทึกรายการ' : 'สร้างรายการ'}
        </Button>
        <Button onClick={handleClose} autoFocus color="error">
          ยกเลิก
        </Button>
        <Button onClick={handleClear} variant="outlined" color="error">
          Clear
        </Button>
      </DialogActions>
    </Dialog>
  );
}